import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import {
  ParticipantsControlType,
  PermissionsSectionDisplayMode,
} from '@gem/call/Model/callModel.types';
import { ICommonPopupsProps } from '@gem/popups';

import { ICallPermissionsPopupProps } from './CallPermissionsPopup.types';

export interface ICallPermissionsPopupPresenter {
  ongoingCall: Call;
  isViewOnly: boolean;
  isShowCustomPermissions: boolean;
  isShowWebinarPermissions: boolean;
  customPermissionsIds: string[];
  canShowAccessForm: boolean;
  permissionsSectionDisplayMode: PermissionsSectionDisplayMode;
  onClose(): void;
}

export const useCallPermissionsPopupPresenter = (
  props: ICommonPopupsProps<ICallPermissionsPopupProps>
): ICallPermissionsPopupPresenter => {
  const { call } = props.params!;

  const presenter = useLocalObservable<ICallPermissionsPopupPresenter>(() => ({
    get ongoingCall(): Call {
      return call;
    },

    get isViewOnly(): boolean {
      return !presenter.ongoingCall?.isMeInitiatorOwnerOrAdmin;
    },

    get isShowCustomPermissions(): boolean {
      return (
        presenter.permissionsSectionDisplayMode === PermissionsSectionDisplayMode.CustomPermissions
      );
    },

    get isShowWebinarPermissions(): boolean {
      return (
        presenter.permissionsSectionDisplayMode === PermissionsSectionDisplayMode.WebinarSpeakers &&
        presenter.ongoingCall.uiFeatures.changeParticipantRole
      );
    },

    get customPermissionsIds(): string[] {
      return presenter.ongoingCall?.permissions.customPermissionsIds || [];
    },

    get canShowAccessForm(): boolean {
      return (
        presenter.ongoingCall.uiFeatures.participantsControlType ===
        ParticipantsControlType.TotalNumber
      );
    },

    get permissionsSectionDisplayMode(): PermissionsSectionDisplayMode {
      return presenter.ongoingCall.uiFeatures.permissionsSectionDisplayMode;
    },

    onClose(): void {
      props.popupInstance.close();
      props.onClose?.();
    },
  }));

  useEffect(() => {
    if (!presenter.ongoingCall.isStarted) {
      presenter.onClose();
    }
  }, [presenter.ongoingCall.isStarted]);

  return presenter;
};
